import React, { ReactElement } from 'react';
import Box from '../../components/Box/Box';
import Icon from '../../components/Icon/Icon';
import { TextRegular } from '../../components/Text';
import getComponentClassNames from '../../utils/className/getComponentClassNames';

export type TStarRatingProps = {
  label?: string;
  ariaLabel?: string;
  /** A value between 0 and 5. */
  rating: number;
};

type TStarRatingSettings = {
  color: string;
  count: number;
};

type TStarsProps = {
  count: number;
  name: string;
} & TStarRatingSettings;

const totalStarsCount = 5;
const color = 'signal-success-default';

const classNames = getComponentClassNames(['StarRating'], ['label']);

function extractStarRatingInfo(rating: number) {
  const filledStarsCount = Math.floor(rating);
  const hasHalfStar = !Number.isInteger(rating);
  return { filledStarsCount, hasHalfStar };
}

function countEmptyStars(total: number, filledStarsCount: number, hasHalfStar: boolean): number {
  return total - filledStarsCount - Number(hasHalfStar);
}

function Stars({ count, ...rest }: TStarsProps): ReactElement[] {
  return Array.from(Array(count)).map((_, index) => <Icon key={String(index)} {...rest} />);
}

function StarRating({ label, ariaLabel, rating, ...rest }: TStarRatingProps): ReactElement {
  const { filledStarsCount, hasHalfStar } = extractStarRatingInfo(rating);
  const labelClassName = `${classNames.label} ${classNames.label}--m`;

  const starRatingSettings = {
    color,
  };

  return (
    <Box as="span" name="StarRating" {...rest}>
      <Stars {...{ count: filledStarsCount, ...starRatingSettings, name: 'saved' }} />
      {hasHalfStar && <Icon name="saved-half" {...starRatingSettings} />}
      <Stars
        {...starRatingSettings}
        count={countEmptyStars(totalStarsCount, filledStarsCount, hasHalfStar)}
        name="save"
      />
      <TextRegular className={labelClassName} aria-label={ariaLabel}>
        {label}
      </TextRegular>
    </Box>
  );
}

export default StarRating;
